import axios from 'axios'
import { supabase } from './supabase'

const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  // FastAPI's Query(list[str]) parameter expects the bracket-less
  // "repeat" format — ?status=a&status=b — but axios defaults to indexed
  // brackets (?status[0]=a&status[1]=b) which FastAPI ignores. Setting
  // indexes: null repeats the key with no brackets. Applies to every
  // request made through this instance; all our backend endpoints follow
  // the FastAPI convention so it's safe globally.
  paramsSerializer: {
    indexes: null,
  },
})

// Request interceptor — attach Bearer token
api.interceptors.request.use(
  async (config) => {
    const { data: { session } } = await supabase.auth.getSession()
    if (session?.access_token) {
      config.headers.Authorization = `Bearer ${session.access_token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor — handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      supabase.auth.signOut()
      window.location.href = '/login'
    }
    if (error.response?.status === 403) {
      console.error('Access denied:', error.response?.data?.message || 'Forbidden')
    }
    return Promise.reject(error)
  }
)

// ─── Order endpoints ─────────────────────────────────────────────────────────

export async function getOrders(params = {}) {
  const { data } = await api.get('/orders', { params })
  return data
}

export async function getOrder(id) {
  const { data } = await api.get(`/orders/${id}`)
  return data
}

export async function exportOrders(params = {}) {
  const response = await api.get('/admin/orders/export', {
    params,
    responseType: 'blob',
  })
  const url = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement('a')
  link.href = url
  const contentDisposition = response.headers['content-disposition']
  const filename = contentDisposition
    ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
    : 'orders-export.csv'
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

// ─── Invoice endpoints ────────────────────────────────────────────────────────

export async function getInvoices() {
  const { data } = await api.get('/invoices')
  return data
}

export async function getInvoicePdf(id) {
  const response = await api.get(`/invoices/${id}/pdf`, {
    responseType: 'blob',
  })
  const url = window.URL.createObjectURL(
    new Blob([response.data], { type: 'application/pdf' })
  )
  window.open(url, '_blank')
  // Revoke after a delay to allow tab to load
  setTimeout(() => window.URL.revokeObjectURL(url), 10000)
}

// ─── Totals endpoint ──────────────────────────────────────────────────────────

export async function getTotals(clientId) {
  const { data } = await api.get(`/totals/${clientId}`)
  return data
}

// ─── Admin endpoints ──────────────────────────────────────────────────────────

// Orders whose ShipStation store maps to no client — chiefly the shared
// Manual Orders store, which cannot be mapped because it serves every client.
// sync.py parks them here instead of discarding them.
// Dismissed rows are hidden by default. They are kept rather than deleted:
// staging upserts on shipstation_order_id, so a deleted row returns on the
// very next scan.
export async function dismissUnassignedOrder(rowId, reason) {
  const { data } = await api.post(`/admin/unassigned-orders/${rowId}/dismiss`, null, {
    params: reason ? { reason } : {},
  })
  return data
}

export async function restoreUnassignedOrder(rowId) {
  const { data } = await api.post(`/admin/unassigned-orders/${rowId}/restore`)
  return data
}

export async function getUnassignedOrders(includeAssigned = false, includeDismissed = false) {
  const { data } = await api.get('/admin/unassigned-orders', {
    params: { include_assigned: includeAssigned, include_dismissed: includeDismissed },
  })
  return data
}

// Attribute one to a client. Creates the real orders row with the TRUE
// ShipStation id; fees are left to the scheduled reprice.
// Stages unmapped orders from a window. Deliberately NOT syncOrders(): the
// full sync pulls a year of shipment costs regardless of window and cannot
// finish inside the gateway timeout. This writes only to the queue.
export async function rescanUnassignedOrders(days = 14) {
  const { data } = await api.post('/admin/unassigned-orders/rescan', null, {
    params: { days },
  })
  return data
}

// Read-only. Asks ShipStation directly about one order number, in ANY status,
// so "not in the queue because it was never shipped" can be told apart from
// "not in the queue because we missed it".
export async function probeShipStationOrder(orderNumber) {
  const { data } = await api.get('/admin/unassigned-orders/probe', {
    params: { order_number: orderNumber },
  })
  return data
}

export async function assignUnassignedOrder(rowId, clientId) {
  const { data } = await api.post(`/admin/unassigned-orders/${rowId}/assign`, {
    client_id: clientId,
  })
  return data
}

export async function getAdminClients(period, opts = {}) {
  // period: { year, month } — when supplied, totals are computed for that
  // historical month instead of the current month-to-date.
  // opts.includeArchived: include archived clients in the result.
  const params = {}
  if (period) {
    params.year  = period.year
    params.month = period.month
  }
  if (opts.includeArchived) params.include_archived = true
  const { data } = await api.get('/admin/clients', { params: Object.keys(params).length ? params : undefined })
  return data
}

export async function getShippingByCarrier(period) {
  const params = period ? { year: period.year, month: period.month } : undefined
  const { data } = await api.get('/admin/shipping-by-carrier', { params })
  return data
}

export async function getArchivedClients() {
  const { data } = await api.get('/admin/clients/archived')
  return data
}

export async function getStores() {
  const { data } = await api.get('/admin/stores')
  return data
}

export async function getMtdCounts(clientId = null) {
  const params = clientId ? { client_id: clientId } : {}
  const { data } = await api.get('/admin/orders/mtd-counts', { params })
  return data
}

export async function getAdminOrders(params = {}) {
  const { data } = await api.get('/admin/orders', { params })
  return data
}

export async function getOrderCarriers(clientId) {
  const { data } = await api.get('/admin/orders/carriers', {
    params: clientId ? { client_id: clientId } : {},
  })
  return data
}

// Distinct carrier + service pairs this client has shipped on, for the service
// picker in Edit Pricing. A year of history: wide enough to cover seasonal
// services the client only uses at peak, still bounded so it can't page the
// whole orders table.
export async function getOrderCarrierServices(clientId, days = 365) {
  const { data } = await api.get('/admin/orders/services', {
    params: { ...(clientId ? { client_id: clientId } : {}), days },
  })
  return data
}

export async function getOrderStates(clientId) {
  const { data } = await api.get('/admin/orders/states', {
    params: clientId ? { client_id: clientId } : {},
  })
  return data
}

export async function getOrderTags(clientId) {
  const { data } = await api.get('/admin/orders/tags', {
    params: clientId ? { client_id: clientId } : {},
  })
  return data
}

export async function createClient(clientData) {
  const { data } = await api.post('/admin/clients', clientData)
  return data
}

export async function getAdminClient(id) {
  const { data } = await api.get(`/admin/clients/${id}`)
  return data
}

export async function updateClient(id, clientData) {
  const { data } = await api.put(`/admin/clients/${id}`, clientData)
  return data
}

export async function getClientPricing(clientId) {
  const { data } = await api.get(`/admin/pricing/${clientId}`)
  return data
}

export async function updatePricing(clientId, pricingData) {
  const { data } = await api.put(`/admin/pricing/${clientId}`, pricingData)
  return data
}

export async function updateOrderCustomInsert(orderIds, hasCustomInsert) {
  const { data } = await api.patch('/admin/orders/custom-insert', {
    order_ids: orderIds,
    has_custom_insert: hasCustomInsert,
  })
  return data
}

export async function updateOrderCasePick(orderIds, hasCasePick, casePickQty = 0, splitRegularItems = 0) {
  const { data } = await api.patch('/admin/orders/case-pick', {
    order_ids: orderIds,
    has_case_pick: hasCasePick,
    case_pick_qty: hasCasePick ? casePickQty : 0,
    split_regular_items: hasCasePick ? splitRegularItems : 0,
  })
  return data
}

export async function updateOrderInsertType(orderId, insertType) {
  const { data } = await api.patch('/admin/orders/insert-type', {
    order_id: orderId,
    insert_type: insertType,
  })
  return data
}

export async function applyInsertDefaultToAllOrders(clientId) {
  const { data } = await api.post(`/admin/orders/apply-insert-default/${clientId}`)
  return data
}

export async function updateOrderFees(orderId, fees) {
  const { data } = await api.patch(`/admin/orders/${orderId}/fees`, fees)
  return data
}

// ─── FedEx Reconciliation ────────────────────────────────────────────────────

export async function uploadFedexPdfs(files) {
  const fd = new FormData()
  for (const f of files) fd.append('files', f)
  const { data } = await api.post('/admin/fedex/upload', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export async function getFedexInvoices(params = {}) {
  const { data } = await api.get('/admin/fedex/invoices', { params })
  return data
}

export async function getFedexInvoiceLines(invoiceId) {
  const { data } = await api.get(`/admin/fedex/invoices/${invoiceId}/lines`)
  return data
}

export async function reprocessFedexInvoice(invoiceId) {
  const { data } = await api.post(`/admin/fedex/invoices/${invoiceId}/reprocess`)
  return data
}

export async function reprocessAllRejectedFedex() {
  const { data } = await api.post('/admin/fedex/reprocess-all-rejected')
  return data
}

// Invoices that are STORED (parse_status='parsed') but whose reconciliation
// never finished — ingest commits the header and lines before it reconciles,
// and deliberately keeps them when that stage fails. Distinct from a rejected
// stub: these need reconciliation re-run, not the PDF re-parsed.
export async function getFedexIncompleteReconciliations() {
  const { data } = await api.get('/admin/fedex/invoices/incomplete-reconciliation')
  return data
}

export async function reconcileFedexInvoice(invoiceId) {
  const { data } = await api.post(`/admin/fedex/invoices/${invoiceId}/reconcile`)
  return data
}

export async function reconcileAllIncompleteFedex() {
  const { data } = await api.post('/admin/fedex/reconcile-all-incomplete')
  return data
}

export async function getFedexReconciliations(params = {}) {
  const { data } = await api.get('/admin/fedex/reconciliations', { params })
  return data
}

export async function updateFedexReconciliationResolution(reconId, resolution) {
  const { data } = await api.patch(
    `/admin/fedex/reconciliations/${reconId}/resolution`,
    { resolution },
  )
  return data
}

export async function assignFedexReconciliation(reconId, orderId) {
  const { data } = await api.patch(
    `/admin/fedex/reconciliations/${reconId}/assign`,
    { order_id: orderId },
  )
  return data
}

export async function getFedexCourierCostOverrides(params = {}) {
  const { data } = await api.get('/admin/fedex/overrides', { params })
  return data
}

export async function getFedexWorkerStatus() {
  const { data } = await api.get('/admin/fedex/worker-status')
  return data
}

// ─── Per-order courier cost (deliverable #10) ─────────────────────────────────
// Powers the courier-cost override modal opened from the admin order table.

export async function getOrderCourierCostSnapshot(orderId) {
  const { data } = await api.get(`/admin/fedex/orders/${orderId}/courier-cost`)
  return data
}

export async function setOrderCourierCostOverride(orderId, { override_cost, reason }) {
  const { data } = await api.patch(
    `/admin/fedex/orders/${orderId}/courier-cost`,
    { override_cost, reason },
  )
  return data
}

export async function clearOrderCourierCostOverride(orderId) {
  const { data } = await api.delete(`/admin/fedex/orders/${orderId}/courier-cost`)
  return data
}

// Download disputed (or any other resolution) recon rows as a CSV evidence pack.
// Mirrors exportOrders's blob → click-anchor pattern.
export async function exportFedexDisputes(resolution = 'disputed') {
  const response = await api.get('/admin/fedex/disputes/export', {
    params: { resolution },
    responseType: 'blob',
  })
  const url = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement('a')
  link.href = url
  const contentDisposition = response.headers['content-disposition']
  const filename = contentDisposition
    ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
    : `fedex-${resolution}-export.csv`
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

// ─── AusPost Reconciliation (Build 2) ───────────────────────────────────────

export async function getAuspostBundles(params = {}) {
  // params: { from, to, show_only, client_id }
  const { data } = await api.get('/admin/auspost/bundles', { params })
  return data
}

export async function updateAuspostItemReview(articleId, { status, notes }) {
  const { data } = await api.patch(
    `/admin/auspost/items/${encodeURIComponent(articleId)}/review`,
    { status, notes },
  )
  return data
}

export async function approveAuspostBundle({ article_ids, status = 'approved', notes }) {
  const { data } = await api.post('/admin/auspost/bundles/approve', {
    article_ids, status, notes,
  })
  return data
}

export async function getAuspostSyncStatus() {
  const { data } = await api.get('/admin/auspost/sync/status')
  return data
}

export async function syncAuspostNow({ period_start, period_end }) {
  const { data } = await api.post('/admin/auspost/sync', { period_start, period_end })
  return data
}

export async function getAuspostWorkerStatus() {
  const { data } = await api.get('/admin/auspost/worker-status')
  return data
}

export async function uploadAuspostInvoice(file, { onUploadProgress } = {}) {
  // multipart/form-data so the FastAPI File() handler picks it up.
  // Long timeout because a 3,000-row CSV's reconciliation phase can take
  // several seconds even with batched writes.
  const form = new FormData()
  form.append('file', file)
  const { data } = await api.post('/admin/auspost/invoices/upload', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 5 * 60_000,
    onUploadProgress,
  })
  return data
}

export async function listAuspostInvoices(params = {}) {
  const { data } = await api.get('/admin/auspost/invoices', { params })
  return data
}

export async function getAuspostInvoice(invoiceId, params = {}) {
  const { data } = await api.get(`/admin/auspost/invoices/${invoiceId}`, { params })
  return data
}

export async function getRolloverSummary(params = {}) {
  const { data } = await api.get('/admin/rollover-summary', { params })
  return data
}

export async function getClientRollover(clientId, params = {}) {
  const { data } = await api.get(`/admin/clients/${clientId}/rollover`, { params })
  return data
}

export async function clearRolloverItems(clientId, { source, ids, invoiceId = null }) {
  const { data } = await api.post(`/admin/clients/${clientId}/rollover/clear`, {
    source, ids, invoice_id: invoiceId,
  })
  return data
}

export async function resetOrderFees(orderId) {
  const { data } = await api.post(`/admin/orders/${orderId}/reset-fees`)
  return data
}

// ─── UPS Reconciliation ──────────────────────────────────────────────────────

export async function uploadUpsInvoice(file) {
  const fd = new FormData()
  fd.append('file', file)
  const { data } = await api.post('/admin/ups/upload', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export async function getUpsInvoices(params = {}) {
  const { data } = await api.get('/admin/ups/invoices', { params })
  return data
}

export async function getUpsInvoiceLines(invoiceId) {
  const { data } = await api.get(`/admin/ups/invoices/${invoiceId}/lines`)
  return data
}

export async function getUpsReconciliations(params = {}) {
  const { data } = await api.get('/admin/ups/reconciliations', { params })
  return data
}

export async function updateUpsReconciliationResolution(reconId, resolution) {
  const { data } = await api.patch(`/admin/ups/reconciliations/${reconId}/resolution`, { resolution })
  return data
}

export async function assignUpsReconciliation(reconId, orderId) {
  const { data } = await api.patch(`/admin/ups/reconciliations/${reconId}/assign`, { order_id: orderId })
  return data
}

export async function getUpsWorkerStatus() {
  const { data } = await api.get('/admin/ups/worker-status')
  return data
}

export async function updateOrderFlatRateException(orderIds, flatRateException) {
  const { data } = await api.patch('/admin/orders/flat-rate-exception', {
    order_ids: orderIds,
    flat_rate_exception: flatRateException,
  })
  return data
}

// ─── Storage endpoints ────────────────────────────────────────────────────────

export async function getStorageRates(clientId) {
  const { data } = await api.get(`/admin/storage/rates/${clientId}`)
  return data
}

export async function updateStorageRates(clientId, payload) {
  const { data } = await api.put(`/admin/storage/rates/${clientId}`, payload)
  return data
}

export async function getStorageEntries(clientId, params = {}) {
  const { data } = await api.get('/admin/storage/entries', {
    params: { client_id: clientId, ...params },
  })
  return data
}

export async function createStorageEntry(payload) {
  const { data } = await api.post('/admin/storage/entries', payload)
  return data
}

export async function updateStorageEntry(entryId, payload) {
  const { data } = await api.patch(`/admin/storage/entries/${entryId}`, payload)
  return data
}

export async function adjustStorageEntry(entryId, payload) {
  const { data } = await api.post(`/admin/storage/entries/${entryId}/adjust`, payload)
  return data
}

export async function deleteStorageEntry(entryId) {
  await api.delete(`/admin/storage/entries/${entryId}`)
}

export async function getStoragePreview(clientId, year, month) {
  const { data } = await api.get('/admin/storage/preview', {
    params: { client_id: clientId, year, month },
  })
  return data
}

// ─── Warehouse endpoints ──────────────────────────────────────────────────────

export async function getSlackChannels(excludeClientId = null) {
  const params = excludeClientId ? { exclude_client_id: excludeClientId } : {}
  const { data } = await api.get('/admin/warehouse/channels', { params })
  return data
}

export async function getWarehouseRates(clientId) {
  const { data } = await api.get(`/admin/warehouse/rates/${clientId}`)
  return data
}

export async function updateWarehouseRates(clientId, payload) {
  const { data } = await api.put(`/admin/warehouse/rates/${clientId}`, payload)
  return data
}

export async function recalculateWarehouseFees(clientId) {
  const { data } = await api.post(`/admin/warehouse/rates/${clientId}/recalculate`)
  return data
}

export async function recalculateAllWarehouseFees() {
  const { data } = await api.post('/admin/warehouse/rates/recalculate/all')
  return data
}

export async function repriceAllClients(opts = {}) {
  // opts: { year, month, includeArchived }
  const params = {}
  if (opts.year)  params.year  = opts.year
  if (opts.month) params.month = opts.month
  if (opts.includeArchived !== undefined) params.include_archived = opts.includeArchived
  const { data } = await api.post('/admin/pricing/reprice-all', null,
    Object.keys(params).length ? { params } : undefined)
  return data
}

export async function getClientDetailReport(clientId, year, month) {
  const { data } = await api.get(`/admin/clients/${clientId}/detail-report`, {
    params: { year, month },
  })
  return data
}

export async function getWarehouseActivities(clientId, params = {}) {
  const { data } = await api.get('/admin/warehouse/activities', {
    params: { client_id: clientId, ...params },
  })
  return data
}

export async function getAllWarehouseActivities(params = {}) {
  const { data } = await api.get('/admin/warehouse/activities/all', { params })
  return data
}

export async function createWarehouseActivity(payload) {
  const { data } = await api.post('/admin/warehouse/activities', payload)
  return data
}

export async function updateWarehouseActivity(activityId, payload) {
  const { data } = await api.patch(`/admin/warehouse/activities/${activityId}`, payload)
  return data
}

export async function deleteWarehouseActivity(activityId) {
  await api.delete(`/admin/warehouse/activities/${activityId}`)
}

export async function syncWarehouseFromSlack(clientId, { fullSync = false } = {}) {
  const { data } = await api.post(`/admin/warehouse/sync/${clientId}`, null, {
    params: { full_sync: fullSync },
  })
  return data
}

// ─── Sync & billing endpoints ─────────────────────────────────────────────────

export async function getSyncStatus() {
  const { data } = await api.get('/sync/status')
  return data
}

export async function getWorkerHealth() {
  const { data } = await api.get('/sync/worker-health')
  return data
}

export async function getWorkerRuns(params = {}) {
  const { data } = await api.get('/sync/worker-runs', { params })
  return data
}

// modifiedAfter re-scans a BOUNDED window, for when an order was processed
// during an outage and is now behind the sync's 24h overlap — it would never
// be looked at again otherwise. Cheaper than full_sync, which reloads the
// whole current year.
export async function syncOrders(fullSync = false, modifiedAfter = null) {
  const params = {}
  if (fullSync) params.full_sync = true
  else if (modifiedAfter) params.modified_after = modifiedAfter
  const { data } = await api.post('/sync/orders', null, { params })
  return data
}

export async function syncXero() {
  const { data } = await api.post('/sync/xero')
  return data
}

export async function runBilling(year, month, dryRun = false, force = false, testMode = false, clientIds = null) {
  // client_ids serializes as repeated `client_ids=a&client_ids=b` via the
  // instance-level paramsSerializer (indexes:null), which FastAPI's
  // Query(list[str]) expects. Omitted when empty -> backend bills all clients.
  const params = { year, month, dry_run: dryRun, force, test_mode: testMode }
  if (clientIds && clientIds.length) params.client_ids = clientIds
  const { data } = await api.post('/billing/run', null, { params })
  return data
}

export async function getXeroSettings() {
  const { data } = await api.get('/billing/xero/settings')
  return data
}

export async function updateXeroSettings(payload) {
  const { data } = await api.patch('/billing/xero/settings', payload)
  return data
}

export async function searchXeroContacts(search) {
  const { data } = await api.get('/billing/xero/contacts', { params: { search } })
  return data
}

export async function disconnectXero() {
  await api.delete('/auth/xero/disconnect')
}

export async function getXeroConnectUrl() {
  const { data } = await api.get('/auth/xero')
  return data.url
}

// ─── SKU rates & order items ──────────────────────────────────────────────────

export async function getSkuRates(clientId) {
  const { data } = await api.get(`/admin/sku-rates/${clientId}`)
  return data
}

export async function saveSkuRates(clientId, rates) {
  const { data } = await api.put(`/admin/sku-rates/${clientId}`, { rates })
  return data
}

export async function getInsertPeriods(clientId) {
  const { data } = await api.get(`/admin/insert-periods/${clientId}`)
  return data
}

export async function saveInsertPeriods(clientId, periods) {
  const { data } = await api.put(`/admin/insert-periods/${clientId}`, { periods })
  return data
}

export async function getSkuCartons(clientId) {
  const { data } = await api.get(`/admin/sku-cartons/${clientId}`)
  return data
}

export async function saveSkuCartons(clientId, cartons) {
  const { data } = await api.put(`/admin/sku-cartons/${clientId}`, { cartons })
  return data
}

export async function getOrderItemsSummary(clientId, params = {}) {
  const { data } = await api.get(`/admin/order-items/${clientId}`, { params })
  return data
}

export async function createManualFulfillment(clientId, payload) {
  const { data } = await api.post(`/admin/fulfillments/${clientId}`, payload)
  return data
}

export async function getFulfillments(clientId, params = {}) {
  const { data } = await api.get(`/admin/fulfillments/${clientId}`, { params })
  return data
}

// ─── Returns endpoints ────────────────────────────────────────────────────────

export const getReturnConfig = (clientId) =>
  api.get(`/admin/return-config/${clientId}`).then(r => r.data)

export const saveReturnConfig = (clientId, data) =>
  api.put(`/admin/return-config/${clientId}`, data).then(r => r.data)

export const getReturnLabels = (clientId, params) =>
  api.get(`/admin/returns/${clientId}`, { params }).then(r => r.data)

// Open a ShipStation-generated return label PDF in a new tab.
// Throws on non-ShipStation labels (the backend returns 404) — callers
// should guard the link with `label.source === 'shipstation'`.
export async function openReturnLabelPdf(labelId) {
  const response = await api.get(`/admin/returns/labels/${labelId}/pdf`, {
    responseType: 'blob',
  })
  const url = window.URL.createObjectURL(
    new Blob([response.data], { type: 'application/pdf' })
  )
  window.open(url, '_blank')
  setTimeout(() => window.URL.revokeObjectURL(url), 10000)
}

// ─── Exports (Zonos duty/tariff fees) ────────────────────────────────────────

export const getExportFees = (clientId, params) =>
  api.get(`/admin/exports/${clientId}`, { params }).then(r => r.data)

export const lookupOrderForExport = (clientId, params) =>
  api.get(`/admin/exports/${clientId}/lookup`, { params }).then(r => r.data)

export const syncExportFees = (from, to) =>
  api.post(`/admin/exports/sync`, null, { params: { from, to } }).then(r => r.data)

export const getUnmatchedExportFees = (params) =>
  api.get(`/admin/exports/unmatched`, { params }).then(r => r.data)

export const getUnmatchedExportsCount = () =>
  api.get(`/admin/exports/unmatched/count`).then(r => r.data)

export const getMatchedExportFees = (params) =>
  api.get(`/admin/exports/matched`, { params }).then(r => r.data)

export const getExportInvoiceSummary = (params) =>
  api.get(`/admin/exports/invoices`, { params }).then(r => r.data)

export const getExportInvoiceDrillIn = (billingDate) =>
  api.get(`/admin/exports/invoices/${billingDate}`).then(r => r.data)

export const assignExportFee = (feeId, clientId, orderNumber) =>
  api.patch(`/admin/exports/${feeId}/assign`, {
    client_id: clientId,
    ...(orderNumber ? { order_number: orderNumber } : {}),
  }).then(r => r.data)

export const createManualExportFee = (clientId, data) =>
  api.post(`/admin/exports/${clientId}/manual`, data).then(r => r.data)

export const updateManualExportFee = (feeId, data) =>
  api.patch(`/admin/exports/manual/${feeId}`, data).then(r => r.data)

export const deleteManualExportFee = (feeId) =>
  api.delete(`/admin/exports/manual/${feeId}`).then(r => r.data)

export const waiveOrder = (orderId, waived) =>
  api.patch(`/admin/orders/${orderId}/waive`, { waived }).then(r => r.data)

export const voidReturnLabel = (labelId, voided) =>
  api.patch(`/admin/returns/${labelId}/void`, { voided }).then(r => r.data)

export const createReturnLabel = (clientId, data) =>
  api.post(`/admin/returns/${clientId}`, data).then(r => r.data)

export const deleteReturnLabel = (labelId) =>
  api.delete(`/admin/returns/${labelId}`).then(r => r.data)

// ─── Postage discrepancies ────────────────────────────────────────────────────

export const getPostageDiscrepancies = (clientId, opts = {}) => {
  // opts: { year, month, allPeriods } — defaults to backend's current month.
  const params = {}
  if (opts.year)  params.year  = opts.year
  if (opts.month) params.month = opts.month
  if (opts.allPeriods) params.all_periods = true
  return api.get(`/admin/postage-discrepancies/${clientId}`,
    Object.keys(params).length ? { params } : undefined).then(r => r.data)
}

export const lookupOrderForDiscrepancy = (clientId, orderNumber) =>
  api.get(`/admin/postage-discrepancies/${clientId}/lookup`, { params: { order_number: orderNumber } }).then(r => r.data)

export const createPostageDiscrepancy = (clientId, data) =>
  api.post(`/admin/postage-discrepancies/${clientId}`, data).then(r => r.data)

export const updatePostageDiscrepancy = (discrepancyId, data) =>
  api.patch(`/admin/postage-discrepancies/entry/${discrepancyId}`, data).then(r => r.data)

// ─── Easyship ─────────────────────────────────────────────────────────────────

export const syncEasyshipLabels = (fromDate, toDate) => {
  // Strip null/undefined so we don't send "to_date=" — see backend comment:
  // a YYYY-MM-DD upper bound is treated as 00:00:00 by Easyship and
  // silently excludes everything created today. Better to omit it entirely
  // so the backend leaves it unbounded.
  const params = {}
  if (fromDate) params.from_date = fromDate
  if (toDate)   params.to_date   = toDate
  return api.post('/admin/easyship/sync', null, { params }).then(r => r.data)
}

export const getUnmatchedEasyshipLabels = () =>
  api.get('/admin/easyship/unmatched').then(r => r.data)

export const getMatchedEasyshipLabels = () =>
  api.get('/admin/easyship/matched').then(r => r.data)

export const getUnmatchedEasyshipCount = () =>
  api.get('/admin/easyship/unmatched/count').then(r => r.data)

export const matchEasyshipLabel = (easyshipLabelId, clientId) =>
  api.post('/admin/easyship/match', {
    easyship_label_id: easyshipLabelId,
    client_id: clientId,
  }).then(r => r.data)

export const dismissEasyshipLabel = (labelId) =>
  api.delete(`/admin/easyship/${labelId}`).then(r => r.data)

export const unpairEasyshipLabel = (returnLabelId) =>
  api.delete(`/admin/easyship/unmatch/${returnLabelId}`).then(r => r.data)

export const deletePostageDiscrepancy = (discrepancyId) =>
  api.delete(`/admin/postage-discrepancies/entry/${discrepancyId}`).then(r => r.data)

// ─── Workbook export ──────────────────────────────────────────────────────────

export async function downloadClientWorkbook(clientId, year, month) {
  const response = await api.get(`/admin/clients/${clientId}/workbook`, {
    params: { year, month },
    responseType: 'blob',
  })
  const url = window.URL.createObjectURL(
    new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
  )
  const link = document.createElement('a')
  link.href = url
  const contentDisposition = response.headers['content-disposition']
  const filename = contentDisposition
    ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
    : `client_${year}_${String(month).padStart(2, '0')}.xlsx`
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

// ─── Packaging tab ─────────────────────────────────────────────────────────

export const getPackagingSummary = (clientId) =>
  api.get(`/admin/clients/${clientId}/packaging/summary`).then(r => r.data)

export const getPackagingTimeseries = (clientId, params = {}) =>
  api.get(`/admin/clients/${clientId}/packaging/timeseries`, { params }).then(r => r.data)

export const listPackageTypes = (clientId) =>
  api.get(`/admin/clients/${clientId}/packaging/types`).then(r => r.data)

export const upsertPackageType = (clientId, packageCode, data) =>
  api.put(
    `/admin/clients/${clientId}/packaging/types/${encodeURIComponent(packageCode)}`,
    data,
  ).then(r => r.data)

export const deletePackageType = (clientId, packageCode) =>
  api.delete(
    `/admin/clients/${clientId}/packaging/types/${encodeURIComponent(packageCode)}`,
  ).then(r => r.data)

export const rematchPackaging = (clientId) =>
  api.post(`/admin/clients/${clientId}/packaging/rematch`).then(r => r.data)

export const listShipstationCustomPackages = () =>
  api.get(`/admin/packaging/shipstation-list`).then(r => r.data)

export const importShipstationPackages = (clientId, packageIds) =>
  api.post(`/admin/clients/${clientId}/packaging/import-shipstation`, {
    package_ids: packageIds,
  }).then(r => r.data)

export const listPackageInventory = (clientId, params = {}) =>
  api.get(`/admin/clients/${clientId}/packaging/inventory`, { params }).then(r => r.data)

export const createPackageInventory = (clientId, data) =>
  api.post(`/admin/clients/${clientId}/packaging/inventory`, data).then(r => r.data)

export const updatePackageInventory = (clientId, entryId, data) =>
  api.patch(`/admin/clients/${clientId}/packaging/inventory/${entryId}`, data).then(r => r.data)

export const deletePackageInventory = (clientId, entryId) =>
  api.delete(`/admin/clients/${clientId}/packaging/inventory/${entryId}`).then(r => r.data)

// ─── Client Admin (/admin/client-users/*) ────────────────────────────────────
// Manage auth.users accounts that log in to the client portal at /account.
// Not the same as /admin/clients which manages client companies.

export async function listClientUsers(params = {}) {
  const { data } = await api.get('/admin/client-users', { params })
  return data
}

export async function getClientUser(userId) {
  const { data } = await api.get(`/admin/client-users/${userId}`)
  return data
}

export async function createClientUser(payload) {
  const { data } = await api.post('/admin/client-users', payload)
  return data
}

export async function updateClientUser(userId, payload) {
  const { data } = await api.patch(`/admin/client-users/${userId}`, payload)
  return data
}

export async function resetClientUserPassword(userId, payload) {
  await api.post(`/admin/client-users/${userId}/reset-password`, payload)
}

export async function disableClientUser(userId) {
  await api.post(`/admin/client-users/${userId}/disable`)
}

export async function enableClientUser(userId) {
  await api.post(`/admin/client-users/${userId}/enable`)
}

// Re-send the account-setup email. The server branches on the account's
// state and returns which kind it sent as `mode`:
//   'invite'   — never confirmed, gets a fresh invite link
//   'recovery' — already active, gets a password reset instead
// The branch matters: re-inviting a confirmed user un-confirms them and
// locks them out of sign-in, so the server refuses to do that any more.
export async function resendClientUserInvite(userId) {
  const { data } = await api.post(`/admin/client-users/${userId}/resend-invite`)
  return data
}

// Force-confirm a user's email. Unblocks the "Email not confirmed" sign-in
// dead end — sends no email and leaves their password alone, so they get
// straight back in with the credentials they already have.
export async function confirmClientUserEmail(userId) {
  await api.post(`/admin/client-users/${userId}/confirm-email`)
}

// Permanent delete — passes the user's email back to the server as a safety
// check; the server compares it to the actual record and refuses if it
// doesn't match. The frontend ALSO requires typing the email into a modal
// before it can fire this. Defense in depth.
export async function deleteClientUser(userId, confirmEmail) {
  await api.delete(`/admin/client-users/${userId}`, {
    params: { confirm_email: confirmEmail },
  })
}

// Client activity log — logins + page navigation events for client portal users.
// Backs the "Client Activity" section on the Client Admin page. Server-side
// paginated + filterable; expected response shape:
//   { events: [...], total: N, page, per_page }
export async function getClientActivity(params = {}) {
  const { data } = await api.get('/admin/client-activity', { params })
  return data
}

// ─── Client portal (/me/*) ────────────────────────────────────────────────────
// Read-only, censored mirrors of the admin endpoints — no carrier_cost,
// no margins. The backend enforces this; these helpers just call them.

export async function getMe() {
  const { data } = await api.get('/me/')
  return data
}

export async function getMeClient(clientId) {
  const { data } = await api.get(`/me/clients/${clientId}`)
  return data
}

export async function getMePricing(clientId) {
  const { data } = await api.get(`/me/clients/${clientId}/pricing`)
  return data
}

export async function getMeSkuRates(clientId) {
  const { data } = await api.get(`/me/clients/${clientId}/sku-rates`)
  return data
}

export async function getMeOrders(clientId, params = {}) {
  const { data } = await api.get(`/me/clients/${clientId}/orders`, { params })
  return data
}

export async function getMeOrderCarriers(clientId) {
  const { data } = await api.get(`/me/clients/${clientId}/orders/carriers`)
  return data
}

export async function getMeOrderStates(clientId) {
  const { data } = await api.get(`/me/clients/${clientId}/orders/states`)
  return data
}

export async function getMeOrderItems(clientId, params = {}) {
  const { data } = await api.get(`/me/clients/${clientId}/order-items`, { params })
  return data
}

export async function getMeFulfillments(clientId, params = {}) {
  const { data } = await api.get(`/me/clients/${clientId}/fulfillments`, { params })
  return data
}

export async function getMeReturnLabels(clientId, params = {}) {
  const { data } = await api.get(`/me/clients/${clientId}/returns/labels`, { params })
  return data
}

export async function openMeReturnLabelPdf(clientId, labelId) {
  const response = await api.get(
    `/me/clients/${clientId}/returns/labels/${labelId}/pdf`,
    { responseType: 'blob' }
  )
  const url = window.URL.createObjectURL(
    new Blob([response.data], { type: 'application/pdf' })
  )
  window.open(url, '_blank')
  setTimeout(() => window.URL.revokeObjectURL(url), 10000)
}

export async function getMeReturnConfig(clientId) {
  const { data } = await api.get(`/me/clients/${clientId}/returns/config`)
  return data
}

export async function getMeExports(clientId, params = {}) {
  const { data } = await api.get(`/me/clients/${clientId}/exports`, { params })
  return data
}

export async function getMeStorageRates(clientId) {
  const { data } = await api.get(`/me/clients/${clientId}/storage/rates`)
  return data
}

export async function getMeStorageEntries(clientId) {
  const { data } = await api.get(`/me/clients/${clientId}/storage/entries`)
  return data
}

export async function getMeStoragePreview(clientId, params = {}) {
  const { data } = await api.get(`/me/clients/${clientId}/storage/preview`, { params })
  return data
}

export async function getMePackagingSummary(clientId) {
  const { data } = await api.get(`/me/clients/${clientId}/packaging/summary`)
  return data
}

export async function getMePackagingTypes(clientId) {
  const { data } = await api.get(`/me/clients/${clientId}/packaging/types`)
  return data
}

export async function getMePackagingInventory(clientId) {
  const { data } = await api.get(`/me/clients/${clientId}/packaging/inventory`)
  return data
}

export async function getMePackagingTimeseries(clientId, params = {}) {
  const { data } = await api.get(`/me/clients/${clientId}/packaging/timeseries`, { params })
  return data
}

export async function getMeWarehouseActivities(clientId, params = {}) {
  const { data } = await api.get(`/me/clients/${clientId}/warehouse/activities`, { params })
  return data
}

export async function getMeWarehouseRates(clientId) {
  const { data } = await api.get(`/me/clients/${clientId}/warehouse/rates`)
  return data
}

export async function getMeDiscrepancies(clientId, params = {}) {
  const { data } = await api.get(`/me/clients/${clientId}/discrepancies`, { params })
  return data
}

export async function getMeMtdCounts(clientId) {
  const { data } = await api.get(`/me/clients/${clientId}/mtd-counts`)
  return data
}

// Per-client month-to-date financial summary — same shape the admin
// ClientDetail cards use (mtd_total + breakdown + outstanding + last_sync),
// stripped of cost/margin internals.
export async function getMeMtdSummary(clientId) {
  const { data } = await api.get(`/me/clients/${clientId}/mtd-summary`)
  return data
}

// Per-day, per-(carrier, service) rollup for the last `days` days.
// Backs the Overview parcel-volume + spend charts. Response is a flat
// list of { date, carrier, service, parcel_count, shipping_cost_total,
// fulfilment_cost_total } — the client pivots it into chart shape.
export async function getMeClientDailyMetrics(clientId, days = 30) {
  const { data } = await api.get(`/me/clients/${clientId}/daily-metrics`, {
    params: { days },
  })
  return data
}

// Overview widget helpers — cycle-time / day-of-week / top-destinations /
// top-skus. All four share the same UTC cutoff + non-waived scope as
// daily-metrics; the frontend renders them side-by-side under the parcels
// and spend charts.

// Order-to-ship latency in hours. Response:
//   { avg_hours, median_hours, sample_count, daily: [{ date, avg_hours, count }] }
export async function getMeClientCycleTime(clientId, days = 30) {
  const { data } = await api.get(`/me/clients/${clientId}/cycle-time`, {
    params: { days },
  })
  return data
}

// Parcel count bucketed by day-of-week. Always returns 7 rows:
//   [{ dow, label, count }, …] with dow 0=Sun..6=Sat.
export async function getMeClientDayOfWeek(clientId, days = 30) {
  const { data } = await api.get(`/me/clients/${clientId}/day-of-week`, {
    params: { days },
  })
  return data
}

// Top recipient states by parcel count. Response:
//   [{ state, count, pct }, …] length ≤ limit, sorted count desc.
export async function getMeClientTopDestinations(clientId, days = 30, limit = 10) {
  const { data } = await api.get(`/me/clients/${clientId}/top-destinations`, {
    params: { days, limit },
  })
  return data
}

// Top SKUs by units shipped (waived orders excluded). Response:
//   [{ sku, name, units, orders, pct_of_units }, …] length ≤ limit.
export async function getMeClientTopSkus(clientId, days = 30, limit = 20) {
  const { data } = await api.get(`/me/clients/${clientId}/top-skus`, {
    params: { days, limit },
  })
  return data
}

// Team management — the only mutation surface on /me/*. See me.py docstring.
export async function getMeTeam(clientId) {
  const { data } = await api.get(`/me/clients/${clientId}/team`)
  return data
}

export async function inviteTeamMember(clientId, payload) {
  const { data } = await api.post(`/me/clients/${clientId}/team/invite`, payload)
  return data
}

export async function removeTeamMember(clientId, memberId) {
  await api.delete(`/me/clients/${clientId}/team/${memberId}`)
}

// Client-side packaging CRUD (carve-out from read-only /me/*).
export async function createMePackagingInventory(clientId, payload) {
  const { data } = await api.post(`/me/clients/${clientId}/packaging/inventory`, payload)
  return data
}
export async function deleteMePackagingInventory(clientId, entryId) {
  await api.delete(`/me/clients/${clientId}/packaging/inventory/${entryId}`)
}
export async function upsertMePackagingType(clientId, packageCode, payload) {
  const { data } = await api.put(
    `/me/clients/${clientId}/packaging/types/${encodeURIComponent(packageCode)}`,
    payload,
  )
  return data
}
export async function deleteMePackagingType(clientId, packageCode) {
  await api.delete(`/me/clients/${clientId}/packaging/types/${encodeURIComponent(packageCode)}`)
}

// ─── Activity tracking ───────────────────────────────────────────────────────

/**
 * Fire-and-forget POST to /me/activity/track — records a user activity event
 * (route view, login, logout) for the admin activity view under Client Admin.
 *
 * Called by:
 *   - components/ClientActivityTracker on every client-portal route change
 *     (debounced 500ms so a redirect chain only logs the final path)
 *   - hooks/useAuth.login on successful sign-in, guarded by client role
 *   - hooks/useAuth.logout before signOut, so the request still carries a
 *     valid Bearer token
 *
 * Uses `fetch` with `keepalive: true` (instead of the shared axios instance)
 * so the request survives tab-close, navigation, and component unmount.
 * The axios variant was dropping pending nav events on short/close-tab
 * sessions — the XHR gets cancelled the moment the page tears down. A
 * keepalive fetch is handed off to the browser and completes in the
 * background. Supported in Chrome 66+, Safari 13+, Firefox 65+; on older
 * browsers fetch throws synchronously and we silently swallow.
 *
 * Errors are swallowed with a console.warn — a failed track must NEVER
 * throw or break the caller. Bearer token is pulled from supabase.auth
 * directly (mirroring the axios request interceptor) since we're bypassing
 * the shared instance.
 */
export async function trackActivity({ event_type, path, client_id }) {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    const headers = { 'Content-Type': 'application/json' }
    if (session?.access_token) {
      headers.Authorization = `Bearer ${session.access_token}`
    }
    await fetch('/api/me/activity/track', {
      method: 'POST',
      headers,
      body: JSON.stringify({ event_type, path, client_id }),
      keepalive: true,
    })
  } catch (err) {
    console.warn('trackActivity failed:', err?.message || err)
  }
}

// Clears the force_password_change flag in BOTH app_metadata and user_metadata
// via the admin API on the server. Called from SetPassword.jsx after the
// user has successfully set their new password. The frontend must NOT clear
// force_password_change in user_metadata itself — user_metadata is
// client-writable and the guard reads app_metadata (which the client cannot
// touch). Errors propagate so the caller can surface them.
export async function completeFirstLogin() {
  const { data } = await api.post('/me/complete-first-login')
  return data
}

// ─── Tracking events (predictive delay) ──────────────────────────────────────

export async function getOrderTrackingEvents(orderId) {
  const { data } = await api.get(`/admin/tracking/orders/${orderId}/events`)
  return data
}

// Complete (un-omitted) event set for the order-table drawer — admin scope.
export async function getOrderTrackingEventsFull(orderId) {
  const { data } = await api.get(`/admin/tracking/orders/${orderId}/events`, {
    params: { full: true },
  })
  return data
}

// Complete event set scoped to the caller's own order — client portal.
export async function getMyOrderTrackingEvents(orderId) {
  const { data } = await api.get(`/me/orders/${orderId}/tracking-events`)
  return data
}

export async function refreshOrderTracking(orderId) {
  const { data } = await api.post(`/admin/tracking/orders/${orderId}/refresh`)
  return data
}

// ─── Tracking dashboard aggregations ─────────────────────────────────────────

/** Headline KPIs + status donut + daily volume + top destinations.
 *  Accepts the same { days, client_id, ... } shape as the other tracking
 *  helpers — anything extra is forwarded as query params. */
export async function getTrackingOverview({ days = 30, ...rest } = {}) {
  const { data } = await api.get('/admin/tracking/overview', { params: { days, ...rest } })
  return data
}

/** Paginated, filterable list of in-flight shipments.
 *  filters: { days, status?:[], carrier?:[], state?:[], min_days?, search?, page, page_size } */
export async function getTrackingInFlight(filters = {}) {
  const { data } = await api.get('/admin/tracking/in-flight', { params: filters })
  return data
}

/** Paginated, filterable list of orders that hit an exception event. */
export async function getTrackingExceptions(filters = {}) {
  const { data } = await api.get('/admin/tracking/exceptions', { params: filters })
  return data
}

/** Heartbeat + 24h stats for the tracking-poll DO Worker. */
export async function getTrackingWorkerStatus() {
  const { data } = await api.get('/admin/tracking/worker-status')
  return data
}

/** Clients with shipments in the lookback window — feeds the page-level
 *  client filter dropdown on the Tracking dashboard. */
export async function getTrackingClients({ days = 90 } = {}) {
  const { data } = await api.get('/admin/tracking/clients', { params: { days } })
  return data
}

/** Per-carrier scorecards (volume, transit std+exp, exception rate, avg cost). */
export async function getTrackingCarriers(filters = {}) {
  const { data } = await api.get('/admin/tracking/carriers', { params: filters })
  return data
}

/** Per-destination-state lane performance with std/exp split and vs-overall delta. */
export async function getTrackingLanes(filters = {}) {
  const { data } = await api.get('/admin/tracking/lanes', { params: filters })
  return data
}

/** Flagged in-flight orders that are overdue vs their lane p90 baseline. */
export async function getTrackingAtRisk(filters = {}) {
  const { data } = await api.get('/admin/tracking/at-risk', { params: filters })
  return data
}

// ─── Rate estimator ──────────────────────────────────────────────────────────
// Multi-carrier shipping-rate quotes via ShipStation v2 / ShipEngine.
//
//   • postAdminRateQuote — admin single quote: sees BOTH carrier_cost and the
//     marked-up client price. Origin defaults to Rosebery but is overridable.
//   • getMeRateQuote      — client quote: origin is locked to Rosebery
//     server-side and the response is profit-censored (price ONLY, no cost).
//   • downloadRateTemplate / postAdminBulkRates — the bulk Excel workflow:
//     download a blank template, fill rows, upload for a costed workbook back.

/** Admin single rate quote — returns rates with carrier_cost AND price. */
export async function postAdminRateQuote(payload) {
  const { data } = await api.post('/admin/rate-estimator/quote', payload)
  return data
}

/** Client rate quote — scoped to the client, price-only (no cost/margin).
 *  ship_from is always the fixed Rosebery origin (set server-side). */
export async function getMeRateQuote(clientId, payload) {
  const { data } = await api.post(`/me/clients/${clientId}/rate-quote`, payload)
  return data
}

/** Download the blank bulk-rate Excel template (blob → click-anchor). */
export async function downloadRateTemplate() {
  const response = await api.get('/admin/rate-estimator/template', {
    responseType: 'blob',
  })
  const url = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement('a')
  link.href = url
  const contentDisposition = response.headers['content-disposition']
  const filename = contentDisposition
    ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
    : 'rate-estimator-template.xlsx'
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

/** Upload a filled template + client_id → download the costed results workbook. */
export async function postAdminBulkRates(clientId, file) {
  const form = new FormData()
  form.append('client_id', clientId)
  form.append('file', file)
  const response = await api.post('/admin/rate-estimator/bulk', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    responseType: 'blob',
  })
  const url = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement('a')
  link.href = url
  const contentDisposition = response.headers['content-disposition']
  const filename = contentDisposition
    ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
    : 'rate-estimator-results.xlsx'
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

export default api

// ── MTD revenue change log ────────────────────────────────────────────────
// Append-only: there are deliberately no update/delete calls here. The table is
// immutable at the DB level; corrections are appended, never edited.

export async function getMtdLedger(params = {}) {
  const { data } = await api.get('/admin/mtd-log', { params })
  return data
}

export async function getMtdLedgerPeriods() {
  const { data } = await api.get('/admin/mtd-log/periods')
  return data
}

export async function getMtdLedgerSummary(period) {
  const { data } = await api.get('/admin/mtd-log/summary', {
    params: period ? { period } : {},
  })
  return data
}

// Per-order breakdown under one operation line, fetched on demand so a
// 2,000-order reprice stays one row in the main table.
export async function getMtdLedgerChildren(lineId) {
  const { data } = await api.get(`/admin/mtd-log/${lineId}/children`)
  return data
}

// Appends 'unattributed'/'month_open' lines so the log sums to live MTD.
// Additive only — it cannot alter or remove an existing line.
export async function sweepMtdLedger(period) {
  const { data } = await api.post('/admin/mtd-log/sweep', null, {
    params: period ? { period } : {},
  })
  return data
}

// ── Products (customs details per client SKU) ─────────────────────────────────
// Admin endpoints 404 for logins outside PRODUCTS_FEATURE_EMAILS; the client
// ones 404 until the client's Products tab is switched on.

export async function getProductsAdminAccess() {
  const { data } = await api.get('/admin/products/access')
  return data
}

export async function getProducts(clientId, mode = 'admin') {
  const base = mode === 'admin' ? '/admin' : '/me'
  const { data } = await api.get(`${base}/clients/${clientId}/products`)
  return data
}

export async function updateProduct(clientId, productId, fields, mode = 'admin') {
  const base = mode === 'admin' ? '/admin' : '/me'
  const { data } = await api.patch(`${base}/clients/${clientId}/products/${productId}`, fields)
  return data
}

export async function uploadProducts(clientId, rows, note, mode = 'admin') {
  const base = mode === 'admin' ? '/admin' : '/me'
  const { data } = await api.post(`${base}/clients/${clientId}/products/upload`, { rows, note: note || null })
  return data
}

export async function confirmProducts(clientId, mode = 'admin') {
  const base = mode === 'admin' ? '/admin' : '/me'
  const { data } = await api.post(`${base}/clients/${clientId}/products/confirm`, { accepted: true })
  return data
}

// Takes a SKU off the list. The row is kept (ignored_at) so the automatic
// product sync doesn't add it back; restoreProduct puts it back.
export async function deleteProduct(clientId, productId) {
  await api.delete(`/admin/clients/${clientId}/products/${productId}`)
}

export async function restoreProduct(clientId, productId) {
  const { data } = await api.post(`/admin/clients/${clientId}/products/${productId}/restore`)
  return data
}

// { changes: [{ field, old_value, new_value, source, note, changed_by_email, changed_by_role, changed_at }] }, newest first.
export async function getProductHistory(clientId, productId) {
  const { data } = await api.get(`/admin/clients/${clientId}/products/${productId}/history`)
  return data
}

export async function importProductsFromShipstation(clientId) {
  const { data } = await api.post(`/admin/clients/${clientId}/products/import-shipstation`)
  return data
}

export async function setProductsClientAccess(clientId, visible) {
  const { data } = await api.put(`/admin/clients/${clientId}/products/client-access`, { visible })
  return data
}

export async function getMeProductsAccess(clientId) {
  const { data } = await api.get(`/me/clients/${clientId}/products/access`)
  return data
}
